/**
 * chartFlow.service — historical Order Flow per-candle bias.
 *
 * Backed by GET /v1/order-flow/history/:symbol; data source is the
 * per-minute delta_trade_buckets populated by orderFlowAggregator.
 * Retention: 7 days. Capped server-side regardless of `fromSec` value.
 */
import api from './api';

export type FlowBias = 'BULL' | 'BEAR' | 'NEUTRAL';
export type FlowWindowBias = 'STRONG_BUY' | 'BUY' | 'NEUTRAL' | 'SELL' | 'STRONG_SELL';
export type FlowAbsorption = 'NONE' | 'SUPPORT_ABSORPTION' | 'RESISTANCE_ABSORPTION';

export interface FlowCandle {
  /** Seconds since epoch, UTC, candle-boundary aligned. */
  time: number;
  buyVol: number;
  sellVol: number;
  buyCount: number;
  sellCount: number;
  buyPct: number;
  bias: FlowBias;
  windowBias: FlowWindowBias;
  absorption: FlowAbsorption;
}

export interface FlowHistoryResponse {
  symbol: string;
  interval: number;
  candles: FlowCandle[];
  srLevelCount?: number;
}

export const chartFlowService = {
  /** Fetch per-candle order-flow bias for a Delta symbol over the requested window.
   *  `fromSec`/`toSec` are seconds since epoch (UTC). Server caps the range at 3 days. */
  async getHistory(symbol: string, fromSec: number, toSec: number, intervalMin: number): Promise<FlowCandle[]> {
    if (!symbol) return [];
    try {
      const r = await api.get<{ success: boolean; data: FlowHistoryResponse }>(`/order-flow/history/${encodeURIComponent(symbol)}`, {
        params: { from: Math.floor(fromSec), to: Math.floor(toSec), interval: Math.floor(intervalMin) },
        headers: { _suppressToast: 'true' },
      });
      return r.data?.data?.candles || [];
    } catch {
      return [];
    }
  },
};
